/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-28
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  IconButton,
  Divider,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { invitationApi } from '../../../api/invitationApi';
import {
  InvitationDeclineFormProps,
  InvitationDeclineRequest,
} from '../../../types/invitation';

/**
 * Form component for declining invitations.
 * Allows optional feedback about the decline reason.
 */
const InvitationDeclineForm: React.FC<InvitationDeclineFormProps> = ({
  invitation,
  token,
  onSuccess,
  onError,
  onBack,
  loading = false,
}) => {
  const { t } = useTranslation('studio');
  const [formData, setFormData] = useState<InvitationDeclineRequest>({
    feedback: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      feedback: event.target.value,
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    setIsSubmitting(true);

    try {
      await invitationApi.declineInvitation(token, formData);
      onSuccess();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to decline invitation';
      onError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    onBack();
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={handleBack} size="small">
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" component="h2">
            {t('invitationForms.decline.title')}
          </Typography>
        </Box>

        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {t('invitationForms.decline.alert', {
              studioName: invitation.studio_name,
              role: invitation.role_display,
            })}
          </Typography>
        </Alert>

        <Divider sx={{ my: 3 }} />

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label={t('invitationForms.decline.feedbackLabel')}
            multiline
            rows={4}
            value={formData.feedback || ''}
            onChange={handleInputChange}
            placeholder={t('invitationForms.decline.feedbackPlaceholder')}
            disabled={isSubmitting || loading}
            sx={{ mb: 3 }}
            helperText={t('invitationForms.decline.feedbackHelper')}
          />

          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={isSubmitting || loading}
            >
              {t('invitationForms.decline.backButton')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="warning"
              disabled={isSubmitting || loading}
              sx={{ minWidth: 120 }}
            >
              {isSubmitting
                ? t('invitationForms.decline.submitting')
                : t('invitationForms.decline.submitButton')}
            </Button>
          </Box>
        </Box>

        <Box mt={3}>
          <Typography variant="body2" color="text.secondary">
            {t('invitationForms.decline.footer')}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default InvitationDeclineForm;
